import React from "react";
import { Wallet, TrendingDown, TrendingUp, PiggyBank } from "lucide-react";
import { useApi } from "../hooks/useApi";

const money = (v) => `${v < 0 ? "-" : ""}$${Math.abs(Number(v || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function Tile({ icon: Icon, label, value, sub, color, testId }) {
  return (
    <div className="glass rounded-2xl p-5" data-testid={testId}>
      <Icon className={`h-6 w-6 ${color}`} />
      <p className="eyebrow mt-3 text-gold">{label}</p>
      <p className={`font-mono-accent text-3xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function Trend({ rows }) {
  const max = Math.max(1, ...rows.map((r) => Math.max(r.income, r.expenses)));
  return (
    <div className="glass rounded-2xl p-5" data-testid="finance-trend">
      <div className="mb-3 flex items-center justify-between"><h4 className="font-serif-display text-lg font-bold text-crimson">Last 12 months</h4>
        <span className="flex gap-3 text-[11px] text-muted-foreground"><span><i className="mr-1 inline-block h-2 w-2 rounded-sm bg-palm" />Income</span><span><i className="mr-1 inline-block h-2 w-2 rounded-sm bg-crimson" />Expenses</span></span></div>
      <div className="flex h-36 items-end gap-1 sm:gap-2">
        {rows.map((r) => (
          <div key={r.month} className="flex flex-1 flex-col items-center gap-1" title={`${r.month}: in ${money(r.income)} · out ${money(r.expenses)}`}>
            <div className="flex w-full items-end justify-center gap-0.5" style={{ height: "120px" }}>
              <div className="w-1/2 rounded-t bg-palm" style={{ height: `${(r.income / max) * 100}%` }} />
              <div className="w-1/2 rounded-t bg-crimson" style={{ height: `${(r.expenses / max) * 100}%` }} />
            </div>
            <span className="font-mono-accent text-[9px] text-muted-foreground">{r.month.slice(5)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Breakdown({ title, map, testId }) {
  const rows = Object.entries(map || {}).sort((a, b) => b[1] - a[1]);
  const total = rows.reduce((s, [, v]) => s + v, 0) || 1;
  return (
    <div className="glass rounded-2xl p-5" data-testid={testId}>
      <h4 className="mb-3 font-serif-display text-lg font-bold text-crimson">{title}</h4>
      {rows.length === 0 && <p className="text-sm text-muted-foreground">Nothing recorded yet this year.</p>}
      <ul className="space-y-2 text-sm">{rows.map(([k, v]) => (
        <li key={k}><div className="flex justify-between"><span>{k}</span><span className="font-mono-accent">{money(v)}</span></div>
          <div className="mt-1 h-1.5 rounded-full bg-gold/20"><div className="h-1.5 rounded-full bg-crimson" style={{ width: `${(v / total) * 100}%` }} /></div></li>))}</ul>
    </div>
  );
}

export function FinancialStanding() {
  const { data } = useApi("/admin/finance/standing");
  if (!data) return null;
  const tm = data.this_month, ytd = data.ytd, cash = data.cash_on_hand;
  return (
    <section className="mb-8" data-testid="financial-standing">
      <h3 className="mb-3 font-serif-display text-2xl font-bold text-crimson">Financial standing</h3>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Tile icon={PiggyBank} label="Cash on hand" value={money(cash.total)} color="text-gold" testId="standing-cash"
          sub={Object.keys(cash.accounts).length ? Object.entries(cash.accounts).map(([a, v]) => `${a} ${money(v.balance)} (${v.month})`).join(" · ") : "Enter statement balances below"} />
        <Tile icon={TrendingUp} label={`Income · ${data.month}`} value={money(tm.income)} sub={`YTD ${money(ytd.income)}`} color="text-palm" testId="standing-income" />
        <Tile icon={TrendingDown} label={`Expenses · ${data.month}`} value={money(tm.expenses)} sub={`YTD ${money(ytd.expenses)}`} color="text-crimson" testId="standing-expenses" />
        <Tile icon={Wallet} label="Net" value={money(tm.net)} sub={`YTD ${money(ytd.net)}`} color={tm.net >= 0 ? "text-palm" : "text-destructive"} testId="standing-net" />
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-1"><Breakdown title="Expenses by category (YTD)" map={ytd.by_category} testId="standing-by-category" /></div>
        <div className="lg:col-span-2"><Trend rows={data.trend} /></div>
      </div>
    </section>
  );
}
